import React, { useState, useEffect } from 'react';
import { DateRange, DashboardMetric } from '../../types';
import Header from './Header';
import DateRangeSelector from './DateRangeSelector';
import MetricCard from '../common/MetricCard';
import { LineChart, BarChart, PieChart } from '../charts';
import DataTable from '../common/DataTable';
import AIInsights from '../insights/AIInsights';
import Button from '../common/Button';
import Card from '../common/Card';
import ConnectionTest from '../common/ConnectionTest';
import { HiRefresh, HiDownload, HiTrendingUp, HiTrendingDown, HiShoppingCart, HiCurrencyDollar, HiUsers, HiChartBar } from 'react-icons/hi';
import { motion } from 'framer-motion';
import {
  mockInsights,
  mockSalesData,
} from '../../data/mockData';
import { dashboardApi } from '../../services/dashboardApi';
import { TableColumn } from '../../types';
import toast from 'react-hot-toast';

type HourlySalesPoint = {
  hour?: number;
  sales?: number;
};

type WeeklySalesPoint = {
  date?: string;
  sales?: number;
};

type CombinedSalesPoint = {
  label: string;
  shopify: number;
  amazon: number;
  total: number;
};

const Dashboard: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: '2024-09-17',
    endDate: '2024-10-17',
    preset: 'last30days',
  });

  // State for all dashboard data
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Fetch all dashboard data
  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await dashboardApi.getAllDashboardData();
      setDashboardData(data);
      setLastUpdated(new Date());
      toast.success('Dashboard data updated successfully!');
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleExport = () => {
    console.log('Exporting dashboard data...');
    toast.success('Export feature coming soon!');
  };

  const handleRefresh = () => {
    console.log('Refreshing dashboard data...');
    fetchAllData();
  };

  // Build metrics from API data
  const buildMetrics = (): DashboardMetric[] => {
    const metrics: DashboardMetric[] = [];

    if (dashboardData?.salesToday) {
      const data = dashboardData.salesToday;
      
      // Combined Sales Today
      metrics.push({
        id: 'combined-sales-today',
        label: 'Combined Sales Today',
        value: parseFloat(data.combinedSalesTotal || '0'),
        change: parseFloat(data.percent_change_vs_yesterday || '0'),
        trend: parseFloat(data.percent_change_vs_yesterday || '0') >= 0 ? 'up' : 'down',
        format: 'currency',
        icon: 'currency',
      });

      // Shopify Sales (calculated)
      const shopifySales = parseFloat(data.combinedSalesTotal || '0') - data.amazonSalesTotalToday;
      metrics.push({
        id: 'shopify-sales-today',
        label: 'Shopify Sales Today',
        value: shopifySales,
        format: 'currency',
        icon: 'shopping',
      });

      // Amazon Sales
      metrics.push({
        id: 'amazon-sales-today',
        label: 'Amazon Sales Today',
        value: data.amazonSalesTotalToday || 0,
        format: 'currency',
        icon: 'shopping',
      });

      // Yesterday's Combined Sales
      metrics.push({
        id: 'yesterday-sales',
        label: 'Yesterday Combined Sales',
        value: parseFloat(data.yesterdayCombinedSalesTotal || '0'),
        format: 'currency',
      });

      // Yesterday Difference
      metrics.push({
        id: 'yesterday-diff',
        label: 'vs Yesterday',
        value: parseFloat(data.yesterdayCombinedDifferent || '0'),
        change: parseFloat(data.yesterdayCombinedPercentage || '0'),
        trend: parseFloat(data.yesterdayCombinedDifferent || '0') >= 0 ? 'up' : 'down',
        format: 'currency',
      });
    }

    if (dashboardData?.dashboard) {
      const data = dashboardData.dashboard;
      
      // Average Order Value
      metrics.push({
        id: 'avg-order-value',
        label: 'Avg Order Value',
        value: data.averageOrderValue || 0,
        format: 'currency',
      });

      // Refunds Today
      if (data.today_refund) {
        metrics.push({
          id: 'refunds-today',
          label: 'Refunds Today',
          value: data.today_refund,
          format: 'currency',
          trend: 'down',
        });
      }
    }

    if (dashboardData?.todayVsLastWeek) {
      const data = dashboardData.todayVsLastWeek;
      
      metrics.push({
        id: 'vs-last-week',
        label: 'vs Last Week Same Day',
        value: data.combinedPercentChange || 0,
        format: 'percentage',
        trend: data.combinedPercentChange >= 0 ? 'up' : 'down',
      });
    }

    return metrics;
  };

  // Build hourly chart data
  const buildHourlyChartData = (): CombinedSalesPoint[] => {
    const shopifyData: HourlySalesPoint[] = normalizeHourly(dashboardData?.hourlySales?.today);
    const amazonData: HourlySalesPoint[] = normalizeHourly(dashboardData?.hourlySalesAmazon?.today);
    
    // Combine data
    const combined = shopifyData.map<CombinedSalesPoint>((item, index) => ({
      label: `${item?.hour ?? index}:00`,
      shopify: item?.sales ?? 0,
      amazon: amazonData[index]?.sales ?? 0,
      total: (item?.sales ?? 0) + (amazonData[index]?.sales ?? 0),
    }));

    return combined;
  };

  const formatNumber = (value: unknown, digits: number = 2): string => {
    const num = typeof value === 'number' ? value : (value == null ? 0 : Number(value));
    const safe = Number.isFinite(num) ? num : 0;
    return safe.toFixed(digits);
  };

  const normalizeHourly = (payload: unknown): HourlySalesPoint[] => {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object') {
      return Object.values(payload as Record<string, HourlySalesPoint>);
    }
    return [];
  };

  // Build weekly chart data
  const buildWeeklyChartData = (): CombinedSalesPoint[] => {
    const shopifyWeek: WeeklySalesPoint[] = dashboardData?.weekSales?.weekSalesData?.dailyBreakdown || [];
    const amazonWeek: WeeklySalesPoint[] = dashboardData?.weekSalesAmazon?.amazonThisWeekdata?.dailyBreakdown || [];
    
    const combined = shopifyWeek.map<CombinedSalesPoint>((item, index) => {
      const date = item.date ? new Date(item.date) : null;
      const label = date && !Number.isNaN(date.getTime())
        ? date.toLocaleDateString('en-US', { weekday: 'short' })
        : `Day ${index + 1}`;

      return {
        label,
        shopify: item.sales ?? 0,
        amazon: amazonWeek[index]?.sales ?? 0,
        total: (item.sales ?? 0) + (amazonWeek[index]?.sales ?? 0),
      };
    });

    return combined;
  };

  const salesColumns: TableColumn[] = [
    {
      id: 'date',
      label: 'Date',
      field: 'date',
      sortable: true,
    },
    {
      id: 'product',
      label: 'Product',
      field: 'productName',
      sortable: true,
    },
    {
      id: 'category',
      label: 'Category',
      field: 'category',
      sortable: true,
    },
    {
      id: 'customer',
      label: 'Customer',
      field: 'customer',
      sortable: true,
    },
    {
      id: 'quantity',
      label: 'Qty',
      field: 'quantity',
      sortable: true,
      align: 'center',
    },
    {
      id: 'revenue',
      label: 'Revenue',
      field: 'revenue',
      sortable: true,
      align: 'right',
      format: (value: number) => `$${value.toFixed(2)}`,
    },
    {
      id: 'profit',
      label: 'Profit',
      field: 'profit',
      sortable: true,
      align: 'right',
      format: (value: number) => `$${value.toFixed(2)}`,
    },
  ];

  const metrics = buildMetrics();
  const hourlyData = buildHourlyChartData();
  const weeklyData = buildWeeklyChartData();
  const hourlyChartData = hourlyData.map(item => ({
    timestamp: item.label,
    label: item.label,
    value: item.total,
  }));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950">
      <Header
        title="Analytics Dashboard"
        subtitle="Real-time insights and automated data integration from Shopify & Amazon"
        actions={
          <>
            <DateRangeSelector dateRange={dateRange} onChange={setDateRange} />
            <ConnectionTest />
            <Button variant="outline" size="sm" onClick={handleRefresh} icon={<HiRefresh />}>
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button variant="primary" size="sm" onClick={handleExport} icon={<HiDownload />}>
              <span className="hidden sm:inline">Export</span>
            </Button>
          </>
        }
      />

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 gap-6">
            <div className="card p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard data...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="card p-6 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800">
            <p className="text-red-600 dark:text-red-400 font-medium">Error: {error}</p>
            <Button variant="primary" size="sm" onClick={handleRefresh} className="mt-4">
              Try Again
            </Button>
          </div>
        )}

        {/* Dashboard Content */}
        {!loading && !error && dashboardData && (
          <>
            {/* Key Metrics */}
            <motion.section
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-8"
            >
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Today's Performance
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                {metrics.map((metric, index) => (
                  <MetricCard key={metric.id} metric={metric} index={index} />
                ))}
              </div>
            </motion.section>

            {/* Comparison Cards */}
            {dashboardData?.todayVsLastWeek && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="mb-8"
              >
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Comparisons
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Today vs Last Week Same Day */}
                  <Card>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">
                          vs Last Week (Same Day)
                        </h3>
                        {dashboardData.todayVsLastWeek.combinedPercentChange >= 0 ? (
                          <HiTrendingUp className="text-green-500 w-6 h-6" />
                        ) : (
                          <HiTrendingDown className="text-red-500 w-6 h-6" />
                        )}
                      </div>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        ${formatNumber(dashboardData.todayVsLastWeek.combinedSameDayTotal, 2)}
                      </p>
                      <p className={`text-sm font-medium ${
                        dashboardData.todayVsLastWeek.combinedPercentChange >= 0 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {dashboardData.todayVsLastWeek.combinedPercentChange >= 0 ? '+' : ''}
                        {formatNumber(dashboardData.todayVsLastWeek.combinedPercentChange, 2)}% 
                        (${formatNumber(Math.abs(Number(dashboardData.todayVsLastWeek.combinedSameDayDiff)) || 0, 2)})
                      </p>
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-600 dark:text-gray-400">Shopify:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            ${formatNumber(dashboardData.todayVsLastWeek.todayVsLastWeekSameDay.salesTotalSameDayLastWeek, 2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Amazon:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            ${formatNumber(dashboardData.todayVsLastWeek.todayVsLastWeekSameDayAmazon.salesTotalSameDayLastWeekAmazon, 2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Shopify Detail */}
                  <Card>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">
                          Shopify Performance
                        </h3>
                        <HiShoppingCart className="text-blue-500 w-6 h-6" />
                      </div>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        ${formatNumber(dashboardData.todayVsLastWeek.todayVsLastWeekSameDay.salesTotalSameDayLastWeek, 2)}
                      </p>
                      <p className={`text-sm font-medium ${
                        dashboardData.todayVsLastWeek.todayVsLastWeekSameDay.percent_change_vs_lastweek >= 0 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {dashboardData.todayVsLastWeek.todayVsLastWeekSameDay.percent_change_vs_lastweek >= 0 ? '+' : ''}
                        {formatNumber(dashboardData.todayVsLastWeek.todayVsLastWeekSameDay.percent_change_vs_lastweek, 2)}%
                      </p>
                    </div>
                  </Card>

                  {/* Amazon Detail */}
                  <Card>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">
                          Amazon Performance
                        </h3>
                        <HiShoppingCart className="text-orange-500 w-6 h-6" />
                      </div>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        ${formatNumber(dashboardData.todayVsLastWeek.todayVsLastWeekSameDayAmazon.salesTotalSameDayLastWeekAmazon, 2)}
                      </p>
                      <p className={`text-sm font-medium ${
                        dashboardData.todayVsLastWeek.todayVsLastWeekSameDayAmazon.percent_change_vs_lastweek_amazon >= 0 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {dashboardData.todayVsLastWeek.todayVsLastWeekSameDayAmazon.percent_change_vs_lastweek_amazon >= 0 ? '+' : ''}
                        {formatNumber(dashboardData.todayVsLastWeek.todayVsLastWeekSameDayAmazon.percent_change_vs_lastweek_amazon, 2)}%
                      </p>
                    </div>
                  </Card>
                </div>
              </motion.section>
            )}

            {/* Quarter Projections */}
            {(dashboardData?.quarterProjection || dashboardData?.quarterProjectionAmazon) && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="mb-8"
              >
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Quarter Projections
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {dashboardData?.quarterProjection && (
                    <Card>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">
                            Shopify Quarter Projection
                          </h3>
                          <HiChartBar className="text-blue-500 w-6 h-6" />
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                          ${typeof dashboardData.quarterProjection === 'object' 
                            ? formatNumber((dashboardData.quarterProjection as any)?.projectedTotal, 2)
                            : formatNumber(dashboardData.quarterProjection as any, 2)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Projected total for this quarter
                        </p>
                      </div>
                    </Card>
                  )}
                  
                  {dashboardData?.quarterProjectionAmazon && (
                    <Card>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase">
                            Amazon Quarter Projection
                          </h3>
                          <HiChartBar className="text-orange-500 w-6 h-6" />
                        </div>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                          ${typeof dashboardData.quarterProjectionAmazon === 'object'
                            ? formatNumber((dashboardData.quarterProjectionAmazon as any)?.projectedTotal, 2)
                            : formatNumber(dashboardData.quarterProjectionAmazon as any, 2)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Projected total for this quarter
                        </p>
                      </div>
                    </Card>
                  )}
                </div>
              </motion.section>
            )}

            {/* AI Insights */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-8"
            >
              <AIInsights insights={mockInsights} />
            </motion.section>

            {/* Charts Row 1 - Hourly and Weekly Sales */}
            {(hourlyData.length > 0 || weeklyData.length > 0) && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mb-8"
              >
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Sales Trends
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {hourlyData.length > 0 && (
                    <LineChart
                      title="Today's Hourly Sales"
                      subtitle="Shopify + Amazon combined by hour"
                      data={hourlyChartData}
                      height={350}
                      color="#0ea5e9"
                    />
                  )}
                  {weeklyData.length > 0 && (
                    <BarChart
                      title="This Week's Sales"
                      subtitle="Daily breakdown (Shopify + Amazon)"
                      data={weeklyData.map(item => ({ label: item.label, value: item.total }))}
                      height={350}
                    />
                  )}
                </div>
              </motion.section>
            )}

            {/* Sales by Platform */}
            {dashboardData?.salesBySKU && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="mb-8"
              >
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Product Performance
                </h2>
                <Card>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Sales by SKU ({dashboardData.salesBySKU.time_range})
                    </h3>
                    <div className="overflow-x-auto">
                      <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {JSON.stringify(dashboardData.salesBySKU.data, null, 2)}
                      </pre>
                    </div>
                  </div>
                </Card>
              </motion.section>
            )}

            {/* Customer Analytics */}
            {dashboardData?.newVsReturningCustomers && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.45 }}
                className="mb-8"
              >
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Customer Analytics
                </h2>
                <Card>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        New vs Returning Customers (Today)
                      </h3>
                      <HiUsers className="text-primary-500 w-8 h-8" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {dashboardData.newVsReturningCustomers.today && (
                        <>
                          <div className="text-center">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">New Customers</p>
                            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                              {dashboardData.newVsReturningCustomers.today.newCustomers || 0}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              {formatNumber(dashboardData.newVsReturningCustomers.today.percentageNew, 1)}%
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Returning Customers</p>
                            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                              {dashboardData.newVsReturningCustomers.today.returningCustomers || 0}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              {formatNumber(dashboardData.newVsReturningCustomers.today.percentageReturning, 1)}%
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Total Customers</p>
                            <p className="text-3xl font-bold text-gray-900 dark:text-white">
                              {dashboardData.newVsReturningCustomers.today.totalCustomers || 0}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.section>
            )}

            {/* Recent Sales Table */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="mb-8"
            >
              <DataTable
                title="Recent Sales"
                subtitle="Latest transactions"
                columns={salesColumns}
                data={mockSalesData}
                pageSize={5}
              />
            </motion.section>

            {/* Raw Data Debug Section (expandable) */}
            <motion.section
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.55 }}
              className="mb-8"
            >
              <details className="card p-6">
                <summary className="cursor-pointer font-semibold text-gray-900 dark:text-white mb-4">
                  View Raw API Data (Debug)
                </summary>
                <div className="mt-4 overflow-x-auto">
                  <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-gray-100 dark:bg-gray-800 p-4 rounded">
                    {JSON.stringify(dashboardData, null, 2)}
                  </pre>
                </div>
              </details>
            </motion.section>

            {/* Footer Info */}
            <motion.section
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="mt-12"
            >
              <div className="card p-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center sm:text-left">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                      Last Updated
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {lastUpdated.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                      Data Sources
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Shopify & Amazon
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                      Auto-Refresh
                    </p>
                    <p className="text-sm font-medium flex items-center justify-center sm:justify-start gap-2">
                      <span className="inline-block w-2 h-2 bg-success-500 rounded-full animate-pulse" />
                      <span className="text-gray-900 dark:text-white">Active</span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.section>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
